import { Component, signal, OnInit, OnDestroy, inject } from '@angular/core';
import { RouterOutlet, Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, MatProgressSpinnerModule, CommonModule],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('SNIPP');
  private router = inject(Router);
  private navigationSubscription?: Subscription;
  
  // Signal to track if navigation is in progress (including resolver execution)
  isLoading = signal(false);
  
  // Track the previous URL to detect if we're navigating within master layout
  private previousUrl: string = '';

  ngOnInit(): void {
    // Subscribe to router events to track navigation state
    this.navigationSubscription = this.router.events
      .pipe(
        filter(
          event =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError
        )
      )
      .subscribe(event => {
        if (event instanceof NavigationStart) {
          const currentUrl = event.url;
          
          // Check if we're navigating within the master layout (child routes)
          // Master layout routes start with paths like: /dashboard, /admin-settings/*, /campaigns, etc.
          const isMasterLayoutRoute = (url: string): boolean => {
            // Exclude login, auth routes, access-denied, and root redirect
            if (url === '/' || 
                url.startsWith('/login') || 
                url.startsWith('/auth/') ||
                url.startsWith('/forgot-password') ||
                url.startsWith('/reset-password') ||
                url.startsWith('/reset-success') ||
                url.startsWith('/forgot-success') ||
                url === '/access-denied') {
              return false;
            }
            // All other routes are within master layout
            return true;
          };
          
          const isPreviousMasterLayout = isMasterLayoutRoute(this.previousUrl);
          const isCurrentMasterLayout = isMasterLayoutRoute(currentUrl);
          
          // Only show loader when:
          // 1. Navigating TO master layout from outside (e.g., login -> dashboard)
          // 2. Navigating FROM master layout to outside (e.g., dashboard -> login)
          // 3. Initial navigation (previousUrl is empty)
          // Do NOT show loader when navigating between child routes within master layout
          const shouldShowLoader = !this.previousUrl || 
                                   (isPreviousMasterLayout !== isCurrentMasterLayout);
          
          if (shouldShowLoader) {
            this.isLoading.set(true);
          }
          
          // Store current URL for next navigation
          this.previousUrl = currentUrl;
        } else if (
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError
        ) {
          // Hide loader when navigation completes, is cancelled, or errors
          this.isLoading.set(false);
          
          // Update previousUrl on successful navigation
          if (event instanceof NavigationEnd) {
            this.previousUrl = event.urlAfterRedirects;
          }
        }
      });
  }

  ngOnDestroy(): void {
    // Clean up subscription
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }
}